import type { RecoverableCheckoutsSummary } from '@/api/recovery'
import type { Environment } from '@/api/types'
import { Card, EmptyState, formatCurrency, formatInteger, KpiCard, LoadingBlock, majorUnits } from '@/design'
import { DEFAULT_WEEKS, useRecoverySummary } from '@/features/analytics/abandonment/recoveryQuery'

function otherCurrency(summary: RecoverableCheckoutsSummary): number {
  return summary.totals.eligible.otherCurrencyCount + summary.totals.ineligible.otherCurrencyCount
}

/**
 * Unfinished checkouts from the recovery report, under its own window: the last
 * eight weeks across every website, whatever dates and website the page is
 * filtered to (the report takes neither). The Abandonment page has the detail.
 */
export function RecoveryTile({ environments, href }: { environments: readonly Environment[]; href: string }) {
  const summary = useRecoverySummary(DEFAULT_WEEKS, environments)
  const data = summary.data
  return (
    <Card
      className="fun-recovery"
      title="Checkout recovery"
      subtitle={`Its own window: the last ${data?.weeks.length ?? DEFAULT_WEEKS} weeks across every website, not the dates above`}
    >
      {summary.isPending ? (
        <LoadingBlock rows={2} label="Loading unfinished checkouts" />
      ) : !data ? (
        <EmptyState variant="outage" compact />
      ) : (
        <>
          <KpiCard
            label={`Unfinished checkouts, last ${data.weeks.length} weeks`}
            value={formatInteger(data.totals.eligible.count + data.totals.ineligible.count)}
            change={null}
            polarity="neutral"
            note={data.truncated ? 'Earlier weeks may be undercounted' : 'Failed or cancelled, one line per competition'}
            detail={`Recoverable ${formatCurrency(majorUnits(data.totals.eligible.valueMinor, data.currency), { currency: data.currency })}`}
            href={href}
          />
          {otherCurrency(data) > 0 ? (
            <p className="fun-note" role="note">
              {formatInteger(otherCurrency(data))} lines were in another currency. They are counted but not included in
              the recoverable value.
            </p>
          ) : null}
        </>
      )}
    </Card>
  )
}
